import {
  getMetrics,
  getFileDetails,
  getFilesList,
} from './file-tracker-service';
import { hasPermissionToRender, modalUtils } from '@/app/core/utils';
import { getCurrentFeatureCode, OktaUserInfo } from '@/app/core/auth';
import { axiosProvider } from '@/app/core/axios';
import { useSetPageTitle } from '@/app/store';
import { useRef, useState, useEffect, SetStateAction } from 'react';
import {
  FileTracker,
  FileTrackerDataProps,
  FileTrackerMetrics,
  FileTrackerListInput,
  FileTrackerListFileProps,
  FileTrackerDetailProps,
  FileTrackerListProps,
  FileTrackerConfigProps,
} from '@ssar-app/file-tracker';
import { CancelTokenSource } from 'axios';
import { FileWithExtraProps } from '@ssar-app/uikit';
import {
  FileTrackerPermissions,
  getShortNameForProduct,
  getSubCodesForProduct,
} from '@ssar-app/domain';
import { isAuthzAnyValue } from '@ascend/authz';
import { useHistory } from 'react-router-dom';
import { usePathContext } from '@ssar-app/okta-authn-react';

export const FileManager = () => {
  useSetPageTitle('File Tracker');
  const history = useHistory();
  const { buildPath } = usePathContext();
  const userProfile = useRef(OktaUserInfo.getOktaUserInfo());
  const profileSubCodes = getSubCodesForProduct(
    getCurrentFeatureCode(),
    userProfile.current,
  );

  const [subCode, setSubCode] = useState<string>('');

  useEffect(() => {
    if (isAuthzAnyValue(profileSubCodes) && subCode === '') {
      setSubCode('');
    } else {
      setSubCode(profileSubCodes.length > 1 ? '' : profileSubCodes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchModel: FileTrackerListInput = {
    clientIdentifier: subCode,
    product: getShortNameForProduct(getCurrentFeatureCode() || ''),
    intentType: [],
    onlyInProgress: false,
  };

  const fetchFileList = (
    fileLimit = 0,
  ): Promise<FileTrackerListProps | Error> => {
    searchModel['limitBy'] = fileLimit > 0 ? fileLimit : undefined;
    return getFilesList(searchModel)
      .then((response) => {
        if (response.resultCode === 0 && response.data?.data) {
          return response.data?.data;
        } else {
          return new Error('Error while getting Files');
        }
      })
      .catch((err) => {
        return new Error(err.message);
      })
      .finally(() => {
        return {
          clientIdentifier: searchModel.clientIdentifier,
          product: searchModel.product,
          files: [],
        };
      });
  };

  const fetchMetrices = (
    selectedOption: number,
  ): Promise<FileTrackerMetrics> => {
    const metrics = {
      stats: {
        avgFileAvailableTime: 0,
        avgFileSize: 0,
        largeFiles: 0,
        totalFiles: 0,
      },
      recentFiles: [],
      fileUploadStats: {
        details: {
          available: 0,
          sampling: 0,
          malwareScan: 0,
          received: 0,
        },
        percentagedetails: {
          available: 0,
          sampling: 0,
          malwareScan: 0,
          received: 0,
        },
        chartData: [],
      },
    };
    searchModel['limitBy'] = undefined;
    return getMetrics(searchModel, selectedOption)
      .then((response) => {
        const metricsData = response.data;
        if (metricsData) {
          return metricsData;
        } else {
          return metrics;
        }
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        return metrics;
      });
  };

  const getFileStatus = (
    record: FileTrackerListFileProps,
  ): Promise<FileTrackerDetailProps | undefined> => {
    const statsData = {
      fileId: record.fileId,
      fileName: record.fileName,
      intentType: record.intentType,
      fileSize: record.fileSize,
      recordCount: record.recordCount,
      uploadSource: record.uploadSource,
      uploadedBy: record.uploadedBy,
      layers: [],
    };
    return getFileDetails(record.fileId)
      .then((response) => {
        const data = response.data;
        if (data) {
          return data.data;
        } else {
          return statsData;
        }
      })
      .catch((err) => {
        modalUtils.showError(err, 'Error while getting File details');
        return statsData;
      })
      .finally(() => {
        return statsData;
      });
  };

  const getUploadStatus = (
    uploadFormData: FormData,
    cancelTokenSource: CancelTokenSource,
    currentFile: FileWithExtraProps,
    setFilesUploadPercentage: (
      value: SetStateAction<FileWithExtraProps[]>,
    ) => void,
  ) => {
    axiosProvider.apiClient
      .post(`/file-tracker/upload`, uploadFormData, {
        cancelToken: cancelTokenSource.token,
        headers: { 'content-type': 'multipart/form-data' },
        onUploadProgress: (data) => {
          const percentage = Math.round((100 * data.loaded) / data.total);
          setFilesUploadPercentage &&
            setFilesUploadPercentage((files) =>
              files.map((file) => {
                if (file.uid === currentFile.uid) {
                  file['percent'] = percentage;
                }
                return file;
              }),
            );
        },
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const fileTrackerConfig: FileTrackerConfigProps = {
    isFileListRequired: hasPermissionToRender(
      getCurrentFeatureCode(),
      FileTrackerPermissions.MayViewDashboard,
    ),
    isMetricesRequired: {
      fileStats: false,
      fileUploadTrend: false,
    },
    isRecentFilesRequired: hasPermissionToRender(
      getCurrentFeatureCode(),
      FileTrackerPermissions.MayViewRecentFiles,
    ),
    isUploadRequired: false,
    isQuickViewRequired: hasPermissionToRender(
      getCurrentFeatureCode(),
      FileTrackerPermissions.MayViewQuickView,
    ),
    subCode: subCode,
    availbleSubCodes: profileSubCodes,
  };

  const fileTrackerDataCalls: FileTrackerDataProps = {
    getFileList: fetchFileList,
    getMetrics: fetchMetrices,
    getUploadStatus: getUploadStatus,
    getFileStatus: getFileStatus,
    exitFileTracker: () => history.push(buildPath('/dashboard?show=jobs')),
    getClientCode: setSubCode,
  };

  return (
    <FileTracker
      fileTrackerConfig={fileTrackerConfig}
      fileTrackerDataCalls={fileTrackerDataCalls}
      key={subCode}
    />
  );
};
